import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Image, Item, Icon, Button, Segment, Label, Dropdown, Message, Dimmer, Loader } from 'semantic-ui-react';
import { StatusLabels } from '../../components/statusTools/StatusIndicators';
import { getPriorityColor, getStatusColor } from '../../constants/statusConfig';
// import characterImage from '../../assets/shutterstock_2294275225-480x480.jpg';
import micAndShieldImage from '../../assets/micAndshield.svg';

import providerImage from '../../assets/shutterstock_2294366549-480x480.png';
import { useSelector, useDispatch } from 'react-redux';
import { setTargetData } from '../../slices/aaiRecordingUtilitySlice';
import { setTargetObj } from '../../slices/aaiCharSequencerSlice';
import RecordingControls from "../../utils/aaiRecordingControls";
import { FaTimes } from 'react-icons/fa';
import { selectActiveDetailObj } from '../../slices/userLitGroupAssetsSlice';
import CharacterItemCard from '../../components/characterDisplay/CharacterItemCard';

// Import selectors from voiceActorSlice
import {
  selectSelectedVariation,
  selectRecordedCharacters
} from '../../slices/voiceActorSlice';

// Import the Voice Lab composite data hook
import useVoiceLabCompositeData from '../../hooks/useVoiceLabCompositeData';

// Import voice actor hooks
import { useVoiceActorUtilities } from '../../hooks/voiceActor';

// Custom RecorderDialog component that works directly within the Voice Lab page
const InlineRecorderDialog = ({ targetData, voiceDetailObj, isOpen, handlePopupClose }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 }); // Initial position
  const [isPlaying, setIsPlaying] = useState(false);
  const popupRef = useRef(null);
  const audioRef = useRef(null);
  const startPosition = useRef({ x: 0, y: 0 });

  const {
    character: {
      character: { name } = { name: "Default Name" }
    } = {},
    variation = "Default Variation",
    textSnippet = { content: "Default text" },
    signedAudioUrl
  } = targetData || {};

  // Determine if we're in playback mode
  const isPlaybackMode = !!signedAudioUrl;

  // Handle audio playback
  useEffect(() => {
    if (isPlaybackMode && audioRef.current) {
      audioRef.current.src = signedAudioUrl;
      audioRef.current.load();
    }
  }, [signedAudioUrl, isPlaybackMode]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle audio events
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  if (!isOpen) return null; // Hide the dialog if not open

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startPosition.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - startPosition.current.x,
      y: e.clientY - startPosition.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div
      ref={popupRef}
      style={{
        position: "fixed", // Changed from absolute to fixed to ensure it's visible
        top: position.y,
        left: position.x,
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        width: "400px",
        zIndex: 2200,
        backgroundColor: "white",
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp} // Stop dragging if the mouse leaves the popup
    >
      <div style={{ marginBottom: "10px", width: "100%", height: "30px", borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#ececec", position: "absolute", top: "0px", left: "0px" }}>
        <Button
          circular
          icon
          className="closeButton"
          style={{ marginTop: "-8px", position: "absolute", top: "8px", right: "8px" }}
          onClick={handlePopupClose} // Close the popup
        >
          <FaTimes className="closeIcon" />
        </Button>
        <div className="fullHeaderFont" style={{ margin: 0, textAlign: "center" }}>
          {isPlaybackMode ? "Play Variation" : "Record Variation"}
        </div>
      </div>
      <div style={{ marginTop: "15px", textAlign: "center" }}>
        <span className="subHeadingFont">{name}</span> - <span className="subHeadingFont">{variation}</span>
      </div>

      {/* Content Section */}
      <div style={{ padding: "5px 0px 0px 0px" }}>
        <Grid style={{ marginBottom: "0px" }}>
          <Grid.Row>
            <Grid.Column className="noMargNoPad">
              {isPlaybackMode ? (
                <div style={{ padding: "15px", textAlign: "center" }}>
                  <audio
                    ref={audioRef}
                    onEnded={handleAudioEnded}
                    style={{ display: "none" }}
                  />
                  <div style={{ marginBottom: "15px", fontStyle: "italic", fontSize: "0.9em" }}>
                    {textSnippet?.content || "No sample text available"}
                  </div>
                  <Button
                    icon
                    labelPosition="left"
                    color={isPlaying ? "red" : "green"}
                    onClick={handlePlayPause}
                  >
                    <Icon name={isPlaying ? "pause" : "play"} />
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                </div>
              ) : (
                <RecordingControls voiceDetailObj={voiceDetailObj} />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
};

// Character Item Component
const CharacterRequestItem = (props) => {
  const { character, index } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [targetData, setLocalTargetData] = useState(null);
  const [playingVariationId, setPlayingVariationId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || 'default-user-id';
  const voiceDetailObj = useSelector(selectActiveDetailObj);
  const reduxSelectedVariation = useSelector(selectSelectedVariation);

  // Add voice actor hooks
  const { getSignedUrl } = useVoiceActorUtilities();

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingVariationId(null);
  };

  // Enhanced record click handler
  const handleRecordClick = (emotion) => {
    // Create target data for the recording dialog
    const newTargetData = {
      character: {
        character: {
          name: character.name,
          uuid: character.uuid
        },
        variation: emotion.name,
        textSnippet: {
          content: emotion.sample_text
        }
      }
    };

    // Set local state for the dialog
    setLocalTargetData(newTargetData);

    // Also dispatch to Redux for other components that might need this data
    dispatch(setTargetData(newTargetData));
    dispatch(setTargetObj(newTargetData));

    // Open the dialog locally
    setIsDialogOpen(true);
  };

  // Add play click handler for recorded variations
  const handlePlayClick = async (emotion) => {
    // If this variation is already playing, pause it
    if (playingVariationId === emotion.variationId && isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      return;
    }

    // Use the emotion object directly since it already has the s3Url
    // This avoids the need to search through character.variations which might be undefined
    if (emotion.s3Url) {
      try {
        // Get a signed URL for playback
        const signedUrl = await getSignedUrl(emotion.s3Url, userId);

        // Set the audio source and play it
        if (audioRef.current) {
          audioRef.current.src = signedUrl;
          audioRef.current.load();
          audioRef.current.play();
          setIsPlaying(true);
          setPlayingVariationId(emotion.variationId);
        }
      } catch (error) {
        console.error('Error getting signed URL:', error);
      }
    } else {
      console.warn('Variation has no audio file:', emotion);
    }
  };

  const handlePopupClose = () => {
    setIsDialogOpen(false);
  };

  // Get the variationsMap from the Redux store at the top level
  const variationsMap = useSelector(state => state.user.voiceActor.characterVariationsMap);

  // Enhanced emotions mapping
  const emotions = useMemo(() => {
    // Debug log to see if variations are available
    console.log(`Character ${character.name} (${character.uuid}) variations:`, character.variations);
    console.log('VariationsMap from Redux:', variationsMap);

    // Always prioritize getting variations from the variationsMap
    if (variationsMap && variationsMap[character.uuid]) {
      console.log(`Found variations in map for character ${character.uuid}:`, variationsMap[character.uuid]);
      return (variationsMap[character.uuid] || []).map(variation => ({
        name: variation.name,
        description: variation.description || "No description available",
        sample_text: variation.content || "No sample text available",
        completed: !!variation.s3Url,
        variationId: variation.uuid,
        characterId: character.uuid, // Add characterId to each variation
        s3Url: variation.s3Url || '',
        version: variation.version || 1
      }));
    }

    // Fallback to character.variations if available
    if (character.variations && character.variations.length > 0) {
      console.log(`Using character.variations for ${character.uuid}:`, character.variations);
      return character.variations.map(variation => ({
        name: variation.name,
        description: variation.description || "No description available",
        sample_text: variation.content || "No sample text available",
        completed: !!variation.s3Url,
        variationId: variation.uuid,
        characterId: character.uuid, // Add characterId to each variation
        s3Url: variation.s3Url || '',
        version: variation.version || 1
      }));
    }

    // If no variations are found, return an empty array
    console.log(`No variations found for character ${character.uuid}`);
    return [];
  }, [character.variations, character.uuid, variationsMap]);

  // Render variations function for the CharacterItemCard
  const renderVariations = () => (
    <>
      {/* Hidden audio element for direct playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: "none" }}
      />

      {emotions.length > 0 ? (
        emotions.map((emotion, emotionIndex) => (
          <div key={emotionIndex} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '10px',
            padding: '10px',
            backgroundColor: emotion.completed ? '#f0fff0' : '#fff',
            borderRadius: '5px',
            border: '1px solid #ddd'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold' }}>{emotion.name}</div>
              <div style={{ fontSize: '0.9em', color: '#666' }}>{emotion.description}</div>
              <div style={{
                marginTop: '5px',
                padding: '8px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                fontStyle: 'italic'
              }}>
                "{emotion.sample_text}"
              </div>
            </div>
            <div style={{ marginLeft: '15px', display: 'flex', gap: '5px' }}>
              {emotion.completed ? (
                <>
                  <Button
                    icon
                    labelPosition='left'
                    size='small'
                    color={isPlaying && playingVariationId === emotion.variationId ? 'red' : 'green'}
                    onClick={() => handlePlayClick(emotion)}
                  >
                    <Icon name={isPlaying && playingVariationId === emotion.variationId ? 'pause' : 'play'} />
                    {isPlaying && playingVariationId === emotion.variationId ? 'Pause' : 'Play'}
                  </Button>
                  <Button
                    icon
                    labelPosition='left'
                    size='small'
                    color='orange'
                    onClick={() => handleRecordClick(emotion)}
                  >
                    <Icon name='redo' />
                    Re-Record
                  </Button>
                </>
              ) : (
                <Button
                  icon
                  labelPosition='left'
                  size='small'
                  color='blue'
                  onClick={() => handleRecordClick(emotion)}
                >
                  <Icon name='microphone' />
                  Record Sample
                </Button>
              )}
            </div>
          </div>
        ))
      ) : (
        <Message info>
          <Message.Header>No Variations Found</Message.Header>
          <p>This character doesn't have any variations yet. Add variations in the Voice Actor Worksheet.</p>
        </Message>
      )}
    </>
  );

  return (
    <>
      <CharacterItemCard
        character={{ ...character, isRequest: true }}
        userId={userId}
        renderVariations={renderVariations}
      />

      {/* Inline recorder dialog */}
      <InlineRecorderDialog
        targetData={targetData}
        voiceDetailObj={voiceDetailObj}
        isOpen={isDialogOpen}
        handlePopupClose={handlePopupClose}
      />
    </>
  );
};

// Provider Profile Component
const ProviderProfile = ({ characters, variationsMap }) => {
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.user.profile);

  // Get all variations from the variationsMap
  const allVariations = Object.values(variationsMap || {}).flat();

  // Calculate stats from real data
  const providerStats = {
    totalRecordings: allVariations.filter(v => v.s3Url).length,
    pendingRequests: (characters || []).length - (characters || []).filter(c => c.status === 'Completed').length,
    completedThisWeek: allVariations.filter(v => {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return v.s3Url && new Date(v.updatedAt) > oneWeekAgo;
    }).length,
    specialties: userProfile?.data?.specialties || ['Audiobooks', 'Animation', 'Character Voices']
  };

  // Handle image loading errors
  const handleImageError = (e) => {
    console.warn('Provider image failed to load');
    e.target.src = providerImage; // Use default image as fallback
  };

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f8f8f8',
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={3}>
            <Image
              src={userProfile?.data?.avatarUrl || providerImage}
              size='small'
              circular
              onError={handleImageError}
            />
          </Grid.Column>
          <Grid.Column width={9} verticalAlign='middle'>
            <div className="fullHeaderFont">Voice Actor {userProfile?.data?.title ? `- ${userProfile.data.title}` : ''}</div>
            <div style={{ marginTop: '10px' }}>
              <Label>
                <Icon name='microphone' />
                {providerStats.totalRecordings} Recordings
              </Label>
              <Label>
                <Icon name='clock' />
                {providerStats.pendingRequests} Pending
              </Label>
              <Label>
                <Icon name='check' />
                {providerStats.completedThisWeek} This Week
              </Label>
            </div>
            <div style={{ marginTop: '10px' }}>
              <strong>Specialties: </strong>
              {providerStats.specialties.join(', ')}
            </div>
          </Grid.Column>
          <Grid.Column width={4} verticalAlign='middle' textAlign='right'>
            <Button color='green'>
              <Icon name='setting' />
              Profile Settings
            </Button>
            <Button
              color='blue'
              style={{ marginTop: '10px' }}
              onClick={() => navigate('/dashboard', { state: { menuSelection: 'VOICE-SAMPLE-WORKSHEET' } })}

            >
              <Icon name='lab' />
              Sample Voice Lab
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <div style={{ marginTop: '15px', textAlign: 'center' }}>
        <Button
          color='blue'
          size='large'
          onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-LAB-MY-RECORDINGS' } })}
        >
          <Icon name='microphone' />
          My Freelance Voices
        </Button>
      </div>
    </div>
  );
};

// Main Component
const VoiceLabCharacterList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);

  // Use the Voice Lab composite data hook to fetch and manage data
  const {
    actorUsers,
    portfolios,
    characters,
    variationsMap,
    isLoading,
    error
  } = useVoiceLabCompositeData({
    forceRefresh: false
  });

  // State for filtered characters
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [portfolioFilter, setPortfolioFilter] = useState('all');

  // Log data status
  console.log('Voice Lab data status:', {
    actorUsersCount: actorUsers.length,
    portfoliosCount: portfolios.length,
    charactersCount: characters.length,
    isLoading,
    hasError: !!error
  });


  // Use a ref to track if we've already processed the current displayCharacters
  const processedCharactersRef = useRef(null);

  // Memoize the filtered characters
  const memoizedFilteredCharacters = useMemo(() => {
    // Skip processing if characters is empty
    if (!characters || characters.length === 0) {
      return [];
    }

    // Use characters for filtering
    let filtered = [...characters];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(character => character.status === statusFilter);
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(character => character.priority === priorityFilter);
    }

    // Apply portfolio filter
    if (portfolioFilter !== 'all') {
      filtered = filtered.filter(character => character.portfolioId === portfolioFilter);
    }

    // Update the ref to track that we've processed these characters
    processedCharactersRef.current = characters;

    return filtered;
  }, [characters, statusFilter, priorityFilter, portfolioFilter]);

  // Update filteredCharacters state when memoizedFilteredCharacters changes
  useEffect(() => {
    setFilteredCharacters(memoizedFilteredCharacters);
  }, [memoizedFilteredCharacters]);

  // Filter handlers
  const handleStatusFilter = (e, { value }) => {
    setStatusFilter(value);
  };

  const handlePriorityFilter = (e, { value }) => {
    setPriorityFilter(value);
  };

  const handlePortfolioFilter = (e, { value }) => {
    setPortfolioFilter(value);
  };

  // Create portfolio options for the dropdown
  const portfolioOptions = useMemo(() => {
    // Start with the "All Portfolios" option
    const options = [{ key: 'all', text: 'All Portfolios', value: 'all' }];

    // Create a Set to track unique portfolio IDs we've already added
    const addedPortfolios = new Set();

    // Add an option for each unique portfolio from the portfolios array
    // This ensures we get all portfolios, not just those referenced by characters
    portfolios.forEach(portfolio => {
      if (portfolio.uuid && !addedPortfolios.has(portfolio.uuid)) {
        addedPortfolios.add(portfolio.uuid);
        options.push({
          key: portfolio.uuid,
          text: portfolio.name || 'Unknown Portfolio',
          value: portfolio.uuid
        });
      }
    });

    // Also add portfolios from characters in case there are any not in the portfolios array
    characters.forEach(character => {
      if (character.portfolioId && !addedPortfolios.has(character.portfolioId)) {
        addedPortfolios.add(character.portfolioId);
        options.push({
          key: character.portfolioId,
          text: character.portfolioName || 'Unknown Portfolio',
          value: character.portfolioId
        });
      }
    });

    return options;
  }, [portfolios, characters]);

  return (
    <>
      {/* Header Section */}
      <div className='listContainer' style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="heading-img-with-no-gradient">
                <Image src={micAndShieldImage} />
              </div>
            </Grid.Column>
            <Grid.Column width={12} textAlign="center">
              <span className="blgListItemTitle">Voice Lab</span>
              <span className="blgListSubTitle">Character Voice Requests</span>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {/* Provider Profile Section */}
      <div className='listContainer'>
        <ProviderProfile characters={characters} variationsMap={variationsMap} />
      </div>

      {/* Voice Request List Heading */}
      <div className='listContainer'>
        <div className="subPageHeaderFont" style={{ marginBottom: '10px' }}>Voice Request List</div>
      </div>

      {/* Filtering Options */}
      <div className='listContainer'>
        <Segment basic>
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by status'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Statuses', value: 'all' },
                    { key: 'new', text: 'New', value: 'New' },
                    { key: 'in-progress', text: 'In Progress', value: 'In Progress' },
                    { key: 'completed', text: 'Completed', value: 'Completed' },
                  ]}
                  onChange={handleStatusFilter}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by priority'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Priorities', value: 'all' },
                    { key: 'high', text: 'High Priority', value: 'High' },
                    { key: 'medium', text: 'Medium Priority', value: 'Medium' },
                    { key: 'low', text: 'Low Priority', value: 'Low' },
                  ]}
                  onChange={handlePriorityFilter}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by portfolio'
                  fluid
                  selection
                  options={portfolioOptions}
                  onChange={handlePortfolioFilter}
                />
              </Grid.Column>
              <Grid.Column width={4} textAlign="right">
                <Button
                  color="blue"
                  icon
                  labelPosition="left"
                  onClick={() => {
                    setStatusFilter('all');
                    setPriorityFilter('all');
                    setPortfolioFilter('all');
                  }}
                >
                  <Icon name="refresh" />
                  Reset Filters
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </div>

      {/* Character Request List */}
      <div className='listContainer'>
        {/* Loading State */}
        {isLoading && (
          <Segment>
            <Dimmer active inverted>
              <Loader inverted>Loading Characters</Loader>
            </Dimmer>
            <Image src='https://react.semantic-ui.com/images/wireframe/short-paragraph.png' />
          </Segment>
        )}

        {/* Error State */}
        {error && (
          <Message negative>
            <Message.Header>Error Loading Characters</Message.Header>
            <p>{error || 'An unknown error occurred'}</p>
          </Message>
        )}

        {/* Success State */}
        {!isLoading && !error && (
          <>
            {actorUsers.length === 0 ? (
              <Message warning>
                <Message.Header>No Voice Actors Found</Message.Header>
                <p>You need to create a voice actor before you can see character requests.</p>
                <Button
                  primary
                  onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTOR-PROFILE' } })}
                  style={{ marginTop: '10px' }}
                >
                  <Icon name='user plus' />
                  Create Voice Actor
                </Button>
              </Message>
            ) : filteredCharacters.length === 0 ? (
              <Message info>
                <Message.Header>No Characters Found</Message.Header>
                <p>
                  {statusFilter === 'all' && priorityFilter === 'all'
                    ? 'There are no characters in the system. Create characters in the Voice Actor Worksheet.'
                    : `There are no characters matching the selected filters. Try changing the filters.`
                  }
                </p>
                <Button
                  primary
                  onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTOR-WORKSHEET' } })}
                  style={{ marginTop: '10px' }}
                >
                  <Icon name='plus' />
                  Go to Voice Actor Worksheet
                </Button>
              </Message>
            ) : (
              <Grid style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}>
                <Grid.Row>
                  <Grid.Column>
                    <Grid columns={2}>
                      {filteredCharacters.map((character, index) => (
                        <Grid.Column key={character.uuid}>
                          <CharacterRequestItem
                            key={character.uuid}
                            index={index}
                            character={character}
                          />
                        </Grid.Column>
                      ))}
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default VoiceLabCharacterList;
